import './pauseMenu.css';

type PauseAction = 'resume' | 'settings' | 'accessibility' | 'return';

interface PauseHandlers {
  resume(): void;
  openAccessibility(): void;
  openSettings(): void;
  returnToTitle(): void;
}

const ACTIONS: readonly PauseAction[] = ['resume', 'settings', 'accessibility', 'return'];

const detailText = (action: PauseAction, confirmingReturn: boolean): string => {
  if (action === 'resume') return 'RESUME THE RECORD.';
  if (action === 'settings') return 'DISPLAY, GRAPHICS, AUDIO, CONTROLS, GAMEPLAY, ACCESSIBILITY.';
  if (action === 'accessibility') return 'COLORBLIND PALETTES, REDUCE-MOTION, TEXT AND EFFECTS QUICKEN HERE.';
  return confirmingReturn
    ? 'CONFIRM RETURN TO TITLE.'
    : 'RETURN TO THE TITLE REGISTER. THE ACTIVE MOMENT WILL BE LEFT WHERE THE LAST RECORD HOLDS.';
};

const labelFor = (action: PauseAction): string => {
  if (action === 'resume') return 'RESUME';
  if (action === 'settings') return 'SETTINGS';
  if (action === 'accessibility') return 'ACCESSIBILITY';
  return 'RETURN TO TITLE';
};

export class PauseMenu {
  private readonly root: HTMLDivElement;
  private readonly detail: HTMLDivElement;
  private buttons: HTMLButtonElement[] = [];
  private handlers: PauseHandlers | null = null;
  private open = false;
  private selected: PauseAction = 'resume';
  private confirmingReturn = false;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'pause-menu';
    this.root.innerHTML = `
      <div class="pm-card">
        <div class="pm-head">FOLIO HOLD</div>
        <div class="pm-body">
          <div class="pm-items"></div>
          <div class="pm-detail"></div>
        </div>
      </div>`;
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.detail = this.root.querySelector('.pm-detail') as HTMLDivElement;
    this.root.addEventListener('click', (event) => this.onClick(event));
    this.root.addEventListener('focusin', (event) => this.onFocus(event));
    window.addEventListener('keydown', (event) => this.onKey(event));
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(handlers: PauseHandlers): void {
    this.handlers = handlers;
    this.selected = 'resume';
    this.confirmingReturn = false;
    this.render();
    this.open = true;
    this.root.style.display = 'flex';
    this.focusSelected();
  }

  hide(): void {
    this.open = false;
    this.confirmingReturn = false;
    this.root.style.display = 'none';
  }

  toggle(handlers: PauseHandlers): void {
    if (this.open) this.hide();
    else this.show(handlers);
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private render(): void {
    const host = this.root.querySelector('.pm-items') as HTMLDivElement;
    host.innerHTML = ACTIONS.map((action) => this.buttonMarkup(action)).join('');
    this.buttons = [...host.querySelectorAll('button')];
    this.updateDetail(this.selected);
  }

  private buttonMarkup(action: PauseAction): string {
    const current = action === this.selected ? ' current' : '';
    return `<button class="pm-item${current}" data-action="${action}">${labelFor(action)}</button>`;
  }

  private onClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!button) return;
    this.activate(button.dataset.action as PauseAction);
  }

  private onFocus(event: FocusEvent): void {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!button) return;
    this.updateDetail(button.dataset.action as PauseAction);
  }

  private onKey(event: KeyboardEvent): void {
    if (!this.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.confirmingReturn) this.updateDetail('return', false);
      else this.handlers?.resume();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveFocus(event.key === 'ArrowDown' ? 1 : -1);
    }
  }

  private moveFocus(step: 1 | -1): void {
    const current = this.buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = current === -1 ? 0 : (current + step + this.buttons.length) % this.buttons.length;
    this.buttons[next]?.focus();
  }

  private focusSelected(): void {
    const button = this.buttons.find((item) => item.dataset.action === this.selected);
    button?.focus();
  }

  private updateDetail(action: PauseAction, confirmingReturn = this.confirmingReturn): void {
    this.selected = action;
    this.confirmingReturn = action === 'return' && confirmingReturn;
    this.detail.className = this.confirmingReturn ? 'pm-detail stamped' : 'pm-detail';
    this.detail.textContent = detailText(action, this.confirmingReturn);
    for (const button of this.buttons) {
      button.classList.toggle('current', button.dataset.action === action);
    }
  }

  private activate(action: PauseAction): void {
    if (!this.handlers) return;
    if (action === 'resume') this.handlers.resume();
    else if (action === 'settings') this.handlers.openSettings();
    else if (action === 'accessibility') this.handlers.openAccessibility();
    else if (action === 'return' && this.confirmingReturn) this.handlers.returnToTitle();
    else this.updateDetail(action, action === 'return');
  }
}
